/* eslint-disable */
// ─── Offline Queue ────────────────────────────────────────────────────────────
// Stores failed progress saves in localStorage and retries when online.
// Rules:
//   - Max 50 items total
//   - Same topicId → merge (keep latest scroll, accumulate time)
//   - Items older than 7 days are discarded on load
//   - Every item carries a recordedAt timestamp so backend can reject stale writes

import { useEffect, useRef, useCallback } from 'react';

const QUEUE_KEY   = 'ebook_offline_queue';
const MAX_ITEMS   = 50;
const MAX_AGE_MS  = 7 * 24 * 60 * 60 * 1000; // 7 days

function loadQueue() {
  try {
    const raw = localStorage.getItem(QUEUE_KEY);
    if (!raw) return [];
    const items = JSON.parse(raw);
    const cutoff = Date.now() - MAX_AGE_MS;
    return items.filter((i) => i.recordedAt > cutoff);
  } catch {
    return [];
  }
}

function saveQueue(items) {
  try {
    localStorage.setItem(QUEUE_KEY, JSON.stringify(items));
  } catch {
    // localStorage full — clear and accept data loss
    localStorage.removeItem(QUEUE_KEY);
  }
}

function mergeIntoQueue(queue, item) {
  const idx = queue.findIndex((q) => q.topicId === item.topicId);
  if (idx !== -1) {
    // Merge: accumulate time, keep max scroll, update timestamp
    const existing = queue[idx];
    const merged = {
      ...existing,
      timeSpentSeconds:  existing.timeSpentSeconds + item.timeSpentSeconds,
      scrollPercent:     Math.max(existing.scrollPercent, item.scrollPercent),
      recordedAt:        item.recordedAt,
      status:            item.status || existing.status,
    };
    const updated = [...queue];
    updated[idx] = merged;
    return updated;
  }

  // New item — enforce max 50, drop oldest if needed
  const appended = [...queue, item];
  if (appended.length > MAX_ITEMS) {
    appended.sort((a, b) => a.recordedAt - b.recordedAt);
    appended.splice(0, appended.length - MAX_ITEMS);
  }
  return appended;
}

export function useOfflineQueue(saveFn) {
  const queueRef    = useRef(loadQueue());
  const flushingRef = useRef(false);

  // Persist queue ref to localStorage whenever it changes
  const persistQueue = useCallback((q) => {
    queueRef.current = q;
    saveQueue(q);
  }, []);

  // Add a failed item to the queue
  const enqueue = useCallback((item) => {
    const withTimestamp = { ...item, recordedAt: item.recordedAt || Date.now() };
    const updated = mergeIntoQueue(queueRef.current, withTimestamp);
    persistQueue(updated);
  }, [persistQueue]);

  // Try to flush all queued items
  const flush = useCallback(async () => {
    if (flushingRef.current) return;
    if (queueRef.current.length === 0) return;
    if (!navigator.onLine) return;

    flushingRef.current = true;
    const queue = [...queueRef.current];

    for (const item of queue) {
      try {
        await saveFn(item);
        // Remove successfully saved item
        const remaining = queueRef.current.filter((q) => q.topicId !== item.topicId || q.recordedAt !== item.recordedAt);
        persistQueue(remaining);
      } catch {
        // Still offline or server error — stop trying, leave in queue
        break;
      }
    }

    flushingRef.current = false;
  }, [saveFn, persistQueue]);

  // Auto-flush when coming back online
  useEffect(() => {
    const handleOnline = () => flush();
    window.addEventListener('online', handleOnline);
    // Also try on mount in case items are already queued and we're online
    flush();
    return () => window.removeEventListener('online', handleOnline);
  }, [flush]);

  return { enqueue, flush, queueLength: queueRef.current.length };
}